import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import "./ProfilePage.css";
import {
  User,
  MapPin,
  Briefcase,
  GraduationCap,
  Award,
  Heart,
  MessageCircle,
  Users,
  Eye,
  Calendar,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
  Camera,
  Loader2,
  MoreVertical,
  Share2,
  Bookmark,
  Settings,
  Plus
} from "lucide-react";

function ProfilePage() {
  const { userid } = useParams();
  console.log(userid);
  
  const emptyProfile = {
    userid: "",
    name: "",
    headline: "",
    about: "",
    location: "",
    profilepic: "",
    contactInfo: {},
    experience: [],
    education: [],
    skills: [],
    connections: 0,
    profileViews: 0,
  };
  
  const [profile, setProfile] = useState(emptyProfile);
  const [userPosts, setUserPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("about");
  const [expandedPosts, setExpandedPosts] = useState({});
  const [postStats, setPostStats] = useState({
    totalLikes: 0,
    totalComments: 0,
    totalPosts: 0
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/profile/${userid}`
        );
        setProfile(res.data);
      } catch (error) {
        console.error("Profile fetch error:", error);
        setProfile(emptyProfile);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userid]);

  useEffect(() => {
    const fetchUserPosts = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/posts/user/${userid}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const myPosts = res.data.filter(post => 
          post.author?.userid === userid || post.author?._id === userid
        );
        
        setUserPosts(myPosts);
        
        const stats = myPosts.reduce((acc, post) => ({
          totalLikes: acc.totalLikes + (post.likes?.length || 0),
          totalComments: acc.totalComments + (post.comments?.length || 0),
          totalPosts: acc.totalPosts + 1
        }), { totalLikes: 0, totalComments: 0, totalPosts: 0 });
        
        setPostStats(stats);
        
      } catch (error) {
        console.error("Posts fetch error:", error);
        setUserPosts([]);
      }
    };

    if (!loading) {
      fetchUserPosts();
    }
  }, [userid, loading]);

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/posts/${postId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const updatedPosts = userPosts.filter(post => post._id !== postId);
      setUserPosts(updatedPosts);
      
      const stats = updatedPosts.reduce((acc, post) => ({
        totalLikes: acc.totalLikes + (post.likes?.length || 0),
        totalComments: acc.totalComments + (post.comments?.length || 0),
        totalPosts: acc.totalPosts + 1
      }), { totalLikes: 0, totalComments: 0, totalPosts: 0 });
      
      setPostStats(stats);
      
    } catch (error) {
      console.error("Delete error:", error);
      alert("Delete failed");
    }
  };

  const toggleComments = (postId) => {
    setExpandedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  if (loading) {
    return (
      <div className="pp-loading">
        <Loader2 size={48} className="pp-spin" />
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="pp-container">
      {/* Background Elements */}
      <div className="pp-bg">
        <div className="pp-gradient"></div>
        <div className="pp-pattern"></div>
      </div>

      {/* Profile Header */}
      <ProfileHeaderComponent profile={profile} userid={userid} />
      
      {/* Impact Stats */}
      <ProfileStats stats={postStats} connections={profile.connections} />
      
      {/* Tabs */}
      <div className="pp-tabs">
        <button 
          className={`pp-tab-btn ${activeTab === "about" ? "active" : ""}`}
          onClick={() => setActiveTab("about")}
        >
          <User size={16} />
          <span>About</span>
        </button>
        <button 
          className={`pp-tab-btn ${activeTab === "experience" ? "active" : ""}`}
          onClick={() => setActiveTab("experience")}
        >
          <Briefcase size={16} />
          <span>Experience</span>
        </button>
        <button 
          className={`pp-tab-btn ${activeTab === "posts" ? "active" : ""}`}
          onClick={() => setActiveTab("posts")}
        >
          <MessageCircle size={16} />
          <span>Posts ({userPosts.length})</span>
        </button>
      </div>
      
      {/* Tab Content */}
      <div className="pp-tab-content">
        {activeTab === "about" && (
          <AboutTabComponent 
            about={profile.about} 
            skills={profile.skills}
          />
        )}
        
        {activeTab === "experience" && (
          <ExperienceTabComponent 
            experience={profile.experience}
            education={profile.education}
          />
        )}
        
        {activeTab === "posts" && (
          <PostsTabComponent 
            posts={userPosts} 
            onDeletePost={handleDeletePost}
            expandedPosts={expandedPosts}
            toggleComments={toggleComments}
          />
        )}
      </div>
    </div>
  );
}

/* ===== PROFILE HEADER COMPONENT ===== */
function ProfileHeaderComponent({ profile, userid }) {
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const isOwnProfile = currentUser?.userid === userid;

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/user/profile-pic`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.reload();
    } catch (err) {
      alert("Image upload failed");
    }
  };

  const getProfileImageUrl = () => {
    if (profile.profilepic) {
      if (profile.profilepic.startsWith('http')) {
        return profile.profilepic;
      } else {
        console.log(`${process.env.REACT_APP_FILE_URL}${profile.profilepic}`);
        return `${process.env.REACT_APP_FILE_URL}${profile.profilepic}`;
      }
    }
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile.userid || 'user'}`;
  };

  return (
    <div className="pp-header">
      {/* Cover Image */}
      <div className="pp-cover">
        <div className="pp-cover-gradient"></div>
      </div>
      
      {/* Profile Picture Section - Separate & Visible */}
      <div className="pp-picture-section">
        <div className="pp-avatar-container">
          <img
            src={getProfileImageUrl()}
            className="pp-avatar"
            alt={profile.name}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile.userid || 'user'}`;
            }}
          />
          {isOwnProfile && (
            <label className="pp-upload-btn">
              <Camera size={18} />
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageUpload}
              />
            </label>
          )}
        </div>
        
        {/* Quick Actions - Optional */}
        {isOwnProfile && (
          <div className="pp-quick-actions">
            <button className="pp-quick-btn">
              <Plus size={16} />
              <span>Add Story</span>
            </button>
          </div>
        )}
      </div>
      
      {/* Profile Info */}
      <div className="pp-info">
        <h1 className="pp-name">{profile.name || "Unnamed User"}</h1>
        <p className="pp-headline">{profile.headline || "No headline provided"}</p>
        
        {profile.location && (
          <div className="pp-location">
            <MapPin size={14} />
            <span>{profile.location}</span>
          </div>
        )}
        
        <div className="pp-action-buttons">
          {isOwnProfile ? (
            <>
              <Link to={`/resume/${userid}`} className="pp-btn-primary">
                <Edit size={16} />
                <span>Edit Profile</span>
              </Link>
              <button className="pp-btn-secondary">
                <Settings size={16} />
              </button>
            </>
          ) : (
            <>
              <button className="pp-btn-primary">
                <Users size={16} />
                <span>Connect</span>
              </button>
              <button className="pp-btn-secondary">
                <MessageCircle size={16} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ===== STATS COMPONENT ===== */
function ProfileStats({ stats, connections }) {
  const statItems = [
    { icon: Heart, label: 'Likes', value: stats.totalLikes, color: '#ef4444' },
    { icon: MessageCircle, label: 'Comments', value: stats.totalComments, color: '#3b82f6' },
    { icon: Users, label: 'Connections', value: connections || 0, color: '#10b981' },
    { icon: Eye, label: 'Views', value: stats.totalPosts * 10 || 0, color: '#8d65dd' }
  ];

  return (
    <div className="pp-stats-grid">
      {statItems.map((stat, index) => (
        <div key={index} className="pp-stat-card">
          <div className="pp-stat-icon" style={{ color: stat.color }}>
            <stat.icon size={24} />
          </div>
          <div className="pp-stat-content">
            <span className="pp-stat-value">{stat.value}</span>
            <span className="pp-stat-label">{stat.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ===== ABOUT TAB ===== */
function AboutTabComponent({ about, skills = [] }) {
  return (
    <div className="pp-tab-pane">
      <div className="pp-info-card">
        <h3>About</h3>
        <p className="pp-about-text">
          {about || "No bio yet. Add a description to tell others about yourself."}
        </p>
      </div>
      
      <div className="pp-info-card">
        <h3>Skills</h3>
        {skills.length === 0 ? (
          <p className="pp-empty-text">No skills listed</p>
        ) : (
          <div className="pp-skills-grid">
            {skills.map((skill, index) => (
              <span key={index} className="pp-skill-tag">
                <Award size={12} />
                {skill.name || skill}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ===== EXPERIENCE TAB ===== */
function ExperienceTabComponent({ experience = [], education = [] }) {
  return (
    <div className="pp-tab-pane">
      <div className="pp-info-card">
        <h3>Experience</h3>
        {experience.length === 0 ? (
          <p className="pp-empty-text">No experience added</p>
        ) : (
          <div className="pp-timeline">
            {experience.map((exp, index) => (
              <div key={index} className="pp-timeline-item">
                <div className="pp-timeline-icon">
                  <Briefcase size={16} />
                </div>
                <div className="pp-timeline-content">
                  <h4>{exp.title}</h4>
                  <p className="pp-timeline-subtitle">{exp.company}</p>
                  <p className="pp-timeline-period">
                    <Calendar size={12} />
                    {exp.period}
                  </p>
                  {exp.description && (
                    <p className="pp-timeline-description">{exp.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="pp-info-card">
        <h3>Education</h3>
        {education.length === 0 ? (
          <p className="pp-empty-text">No education added</p>
        ) : (
          <div className="pp-timeline">
            {education.map((edu, index) => (
              <div key={index} className="pp-timeline-item">
                <div className="pp-timeline-icon">
                  <GraduationCap size={16} />
                </div>
                <div className="pp-timeline-content">
                  <h4>{edu.school}</h4>
                  <p className="pp-timeline-subtitle">{edu.degree}</p>
                  <p className="pp-timeline-period">
                    <Calendar size={12} />
                    {edu.period}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ===== POSTS TAB ===== */
function PostsTabComponent({ posts, onDeletePost, expandedPosts, toggleComments }) {
  const currentUser = JSON.parse(localStorage.getItem("user"));

  const getAuthorImageUrl = (author) => {
    if (author?.profilepic) {
      if (author.profilepic.startsWith('http')) {
        return author.profilepic;
      } else {
        return `${process.env.REACT_APP_FILE_URL}${author.profilepic}`;
      }
    }
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${author?.userid || 'user'}`;
  };

  const getCommenterImageUrl = (user) => {
    if (user?.profilepic) {
      if (user.profilepic.startsWith('http')) {
        return user.profilepic;
      } else {
        return `${process.env.REACT_APP_FILE_URL}${user.profilepic}`;
      }
    }
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.userid || 'commenter'}`;
  };

  if (posts.length === 0) {
    return (
      <div className="pp-tab-pane">
        <div className="pp-empty-state">
          <MessageCircle size={48} className="pp-empty-icon" />
          <h3>No posts yet</h3>
          <p>Your posts will appear here</p>
          <Link to="/feed" className="pp-btn-primary">
            Create post
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pp-tab-pane">
      <div className="pp-posts-header">
        <h3>Your Posts</h3>
        <span className="pp-posts-count">{posts.length} total</span>
      </div>
      
      <div className="pp-posts-feed">
        {posts.map((post) => (
          <div key={post._id} className="pp-post-card">
            {/* Post Header */}
            <div className="pp-post-header">
              <div className="pp-post-author">
                <img 
                  src={getAuthorImageUrl(post.author)}
                  className="pp-post-avatar"
                  alt={post.author?.name || "author"}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${post.author?.userid || 'user'}`;
                  }}
                />
                <div className="pp-post-author-info">
                  <span className="pp-post-author-name">{post.author?.name || "You"}</span>
                  <span className="pp-post-date">
                    <Calendar size={12} />
                    {post.createdAt ? new Date(post.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    }) : 'Recent'}
                  </span>
                </div>
              </div>
              
              <div className="pp-post-actions">
                <button className="pp-post-action-btn">
                  <Share2 size={16} />
                </button>
                <button className="pp-post-action-btn">
                  <Bookmark size={16} />
                </button>
                {currentUser?.userid === post.author?.userid && (
                  <button 
                    className="pp-post-action-btn delete"
                    onClick={() => onDeletePost(post._id)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
                <button className="pp-post-action-btn">
                  <MoreVertical size={16} />
                </button>
              </div>
            </div>
            
            {/* Post Content */}
            <div className="pp-post-content">
              <p className="pp-post-text">{post.content || post.text || "No content"}</p>
            </div>
            
            {/* Post Image */}
            {post.image && (
              <div className="pp-post-image">
                <img 
                  src={post.image.startsWith('http') ? post.image : `${process.env.REACT_APP_FILE_URL}${post.image}`} 
                  alt="Post content"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
            
            {/* Post Stats */}
            <div className="pp-post-stats">
              <span className="pp-stat-item">
                <Heart size={14} />
                <span className="pp-stat-number">{post.likes?.length || 0}</span>
                <span className="pp-stat-label">likes</span>
              </span>
              <span className="pp-stat-item">
                <MessageCircle size={14} />
                <span className="pp-stat-number">{post.comments?.length || 0}</span>
                <span className="pp-stat-label">comments</span>
              </span>
              {post.views && (
                <span className="pp-stat-item">
                  <Eye size={14} />
                  <span className="pp-stat-number">{post.views}</span>
                  <span className="pp-stat-label">views</span>
                </span>
              )}
            </div>

            {/* Comments Section */}
            {post.comments && post.comments.length > 0 && (
              <div className="pp-post-comments">
                <button 
                  className="pp-comments-toggle"
                  onClick={() => toggleComments(post._id)}
                >
                  {expandedPosts[post._id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  <span>
                    {expandedPosts[post._id] ? 'Hide' : 'View'} {post.comments.length} comment{post.comments.length > 1 ? 's' : ''}
                  </span>
                </button>
                
                {expandedPosts[post._id] && (
                  <div className="pp-comments-list">
                    {post.comments.map((comment, idx) => (
                      <div key={idx} className="pp-comment-item">
                        <img 
                          src={getCommenterImageUrl(comment.user)}
                          className="pp-comment-avatar"
                          alt={comment.user?.name || "commenter"}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${comment.user?.userid || 'commenter'}`;
                          }}
                        />
                        <div className="pp-comment-content">
                          <div className="pp-comment-header">
                            <span className="pp-comment-author">
                              {comment.user?.name || 'User'}
                            </span>
                            <span className="pp-comment-time">
                              {comment.createdAt ? new Date(comment.createdAt).toLocaleDateString() : ''}
                            </span>
                          </div>
                          <p className="pp-comment-text">{comment.text || comment.content || comment.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {post.comments?.length === 0 && (
              <div className="pp-no-comments">
                <MessageCircle size={14} />
                <span>No comments yet</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default ProfilePage;